import type { ReactElement } from 'react';
import type {
  MCPServerListItem,
  MCPServerUserVersion,
  MCPVersionEntry,
} from '@kirin/api-client/contracts/mcp';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TruncatedText } from '@/components/shared/TruncatedText';
import { MCPVersionDetailsPopover } from './MCPVersionDetailsPopover';
import {
  deriveMcpVersionState,
  NO_USER_VERSION,
  showableVersions,
  versionOriginLabel,
} from './mcpVersionState';

interface MCPVersionDisplayProps {
  versions: (string | null)[];
}

/**
 * The Version column cell (KIR-4844). Prints the headline version, a `+N` for
 * the rest of the group, or an em dash when there is nothing to print. Every
 * version carries its origin on hover — which resolver read it, or that the
 * server declared it itself — so an installed version and a handshake claim are
 * never confused for one another.
 *
 * The cell is ~160px, so the version gets the width and every qualifier is a
 * tooltip or lives in the details popover.
 */
export function MCPVersionCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  const state = deriveMcpVersionState(row);

  // Hidden only when nothing is behind it. Counts SHOWABLE versions, not raw
  // ones: an entry the cell suppressed is suppressed in the panel too, so a
  // row whose only version was dropped and that has no resolution method has
  // nothing to open — the button would sit beside an em dash and explain
  // nothing. A method alone still earns it: that is what the panel explains.
  const hasDetails =
    showableVersions(row.versions).length > 0 ||
    (row.resolutionMethods?.length ?? 0) > 0;

  return (
    <span className="flex w-full min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="min-w-0 flex-1">
        <MCPVersionStateDisplay state={state} />
      </span>
      {hasDetails && <MCPVersionDetailsPopover row={row} />}
    </span>
  );
}

/**
 * One USER's version, for the Users popup's Version column (KIR-5042).
 *
 * Rendered through the same `versionOriginLabel` the cell uses, so a version
 * looks and reads identically in both places — the origin marker beside it is
 * what stops a handshake claim being mistaken for a reading off an artifact,
 * and it must not be present in one surface and absent in the other.
 *
 * `null` is the common answer, not an error: a remote server has no installed
 * copy and a never-spawned one has nothing downloaded yet. A self-declared
 * version is shown the same way, as an em dash (KIR-5222).
 */
export function MCPUserVersion({
  version,
}: {
  version: MCPServerUserVersion | null | undefined;
}): ReactElement {
  // A handshake claim is not a version (KIR-5222): same em dash as no version,
  // with the claim kept in the tooltip, labelled for what it is.
  if (version == null || version.source === 'self-declared') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default text-muted-foreground">—</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-0.5">
          <div className="font-medium">{NO_USER_VERSION.label}</div>
          <div>{NO_USER_VERSION.description}</div>
          {version != null && (
            <div className="text-muted-foreground">
              {versionOriginLabel(version)}: {version.version}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block min-w-0 cursor-default">
          <TruncatedText>{version.version}</TruncatedText>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-0.5">
        <div className="font-medium">{version.version}</div>
        <div className="text-muted-foreground">
          {versionOriginLabel(version)}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * One version plus the provenance line every tooltip repeats.
 *
 * Deliberately two lines plus the spec: the resolution method and the floating
 * re-resolution note read as jargon in a hover, and both already have a row in
 * the details popover ("How we know", "Updates") where there is room to explain
 * them. The tooltip answers only "what version, and can I trust it".
 */
function VersionOrigin({ entry }: { entry: MCPVersionEntry }): ReactElement {
  return (
    <>
      <div className="font-medium">{entry.version}</div>
      <div className="text-muted-foreground">{versionOriginLabel(entry)}</div>
      {entry.declaredSpec && (
        <div className="text-muted-foreground">from {entry.declaredSpec}</div>
      )}
    </>
  );
}

function MCPVersionStateDisplay({
  state,
}: {
  state: ReturnType<typeof deriveMcpVersionState>;
}): ReactElement {
  if (state.kind === 'none') {
    // One shape for every "no version" case so the column stays quiet; the
    // reason is a hover away rather than a badge competing with real versions.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">—</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="font-medium">{state.label}</div>
          <div>{state.description}</div>
        </TooltipContent>
      </Tooltip>
    );
  }

  const headline = state.headline;
  // One pick per user, DEDUPED across users (KIR-5042): `+N` counts the
  // distinct versions people are on, not the people. Five users all on two
  // versions read `+1`, not `+4`. The dedupe is what keeps a user with an
  // install and their own weaker handshake claim from putting a `+1` on a
  // single-user row.
  const others = state.userEntries.slice(1);

  return (
    <span className="flex min-w-0 items-center gap-1">
      {/* min-w-0 WITHOUT flex-1: the wrapper sizes to the number so the +N sits
          against it instead of being pushed to the far edge, and min-w-0 still
          lets it truncate rather than overflow. Only the badge competes for the
          width, and it is the one thing allowed to win. */}
      <span className="min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block min-w-0 cursor-default">
              <TruncatedText>{headline.version}</TruncatedText>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs space-y-0.5">
            <VersionOrigin entry={headline} />
          </TooltipContent>
        </Tooltip>
      </span>
      {others.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className="shrink-0 cursor-help px-1.5 py-0 text-xs"
            >
              +{others.length}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {/* Every version listed with its own origin: a group spans machines
                and users, and a bare list cannot say which is which. The
                per-user list, matching the badge — the panel is where the full
                evidence list lives. */}
            <div className="space-y-2">
              {state.userEntries.map((entry, i) => (
                <div key={`${entry.source}-${entry.version}-${i}`}>
                  <VersionOrigin entry={entry} />
                </div>
              ))}
            </div>
            {/* Which version belongs to whom is attribution, and attribution
                lives where the users are. Naming them here would duplicate that
                table in a tooltip too narrow to hold it. */}
            <div className="mt-2 border-t pt-1.5 text-muted-foreground">
              Open the Users column to see who is on which version.
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

/**
 * Renders version strings: the first non-null as primary, a `+N` badge for the
 * rest. Agnostic about which — callers pass installed entries, already ordered.
 */
export function MCPVersionDisplay({
  versions,
}: MCPVersionDisplayProps): ReactElement {
  // Primary = first existing (non-null) version, not necessarily versions[0].
  const primary = versions.find(v => v != null);

  // No versions or all null
  if (primary == null) {
    return <span>—</span>;
  }

  // Single version - truncate with a clip-only tooltip showing the full string.
  if (versions.length === 1) {
    return <TruncatedText>{primary}</TruncatedText>;
  }

  // Multiple versions - truncate the primary; the +N badge carries the
  // all-versions tooltip and never shrinks.
  return (
    <div className="flex items-center gap-1 min-w-0">
      <TruncatedText className="w-auto min-w-0">{primary}</TruncatedText>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className="text-xs px-1.5 py-0 cursor-help shrink-0"
          >
            +{versions.length - 1}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <div className="font-medium">All versions:</div>
            {versions.map((v, i) => (
              <div key={i} className="text-sm">
                {v || 'Unknown'}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
